import * as React from 'react';
import cx from 'classnames';
import { LinkProps as RouterLinkProps } from 'react-router-dom';
import RouterLink from './RouterLink';
import { addClassName } from '@elevio/styles/lib/utils/classes';
import { getBaseUrl } from '../utils/globals';
import useCategoryFragment from '../hooks/useCategoryFragment';
import useIsCategoryActive from '../hooks/useIsCategoryActive';
import useSubCategories from '../hooks/useSubCategories';
import { Category } from './categoryFetch';
import { CategoryContext } from '../contexts';
import { TotalCount } from './articles';
import { ICategory } from '../types';

export { TotalCount as ArticleCount, Category as Fetch };

type TitleProps = {
  categoryId?: string;
};
/**
 * Render the title of a category, if you don't pass in a categoryId
 * then it will use the current category.
 * This will fetch the category if we don't have it already.
 */
export const Title: React.SFC<TitleProps> = ({ categoryId }) => {
  const category = useCategoryFragment(categoryId);
  return <>{category && category.title}</>;
};

type ImageUrlProps = {
  className?: string;
  categoryId?: string;
};
/**
 * Render the featured Image Url of a category, if you don't pass in a categoryId
 * then it will use the current category.
 * This will fetch the category if we don't have it already.
 */
export const FeaturedImageUrl: React.SFC<ImageUrlProps> = ({
  className,
  categoryId,
}) => {
  const category = useCategoryFragment(categoryId);
  return !category ? null : (
    <img
      src={category.featuredImageUrl || ''}
      className={cx(className, addClassName('featured-image'))}
    />
  );
};

type LinkProps = {
  categoryId?: string;
  slug?: string;
  'data-testid'?: string;
} & Omit<RouterLinkProps, 'to'>;
/**
 * This will render an A tag with a href that points to a category page.
 * If you pass in a categoryId then it will use that otherwise it will
 * use the current category context.
 */
export const Link: React.FC<LinkProps> = ({
  children,
  categoryId,
  slug,
  className,
  'data-testid': dataTestId,
  ...props
}) => {
  const baseUrl = getBaseUrl();
  const category = useCategoryFragment(categoryId);
  const _slug = slug ? slug : category ? category.slug : categoryId;
  if (!_slug) return null;

  return (
    <RouterLink
      {...props}
      className={cx(className, addClassName('category-link'))}
      to={`${baseUrl}/categories/${_slug}`}
      data-testid={dataTestId}
    >
      {children}
    </RouterLink>
  );
};

// type ParentProps = {};
// /**
//  * This set's the current category context to be the parent of the current category
//  * (or the categoryId that is passed in).
//  * TODO: should I allow a render prop?
//  */
// export const Parent: React.FC<ParentProps> = ({ children }) => {
//   const category = useCategory();
//   if (!category) return null;
//   return (
//     <CategoryFragmentContext.Provider
//       value={
//         category && category.parentCategory
//           ? category.parentCategory
//           : undefined
//       }
//     >
//       {children}
//     </CategoryFragmentContext.Provider>
//   );
// };

type IsActiveProps = {
  categoryId?: string;
  children: ((isActive: boolean) => React.ReactNode) | React.ReactNode;
};
/**
 * Renders out the child content if the category either from the categoryId prop or if not provided the
 * current category is active
 * If a render prop is passed as the child then a boolean will be provided with the is active state
 * NOTE: see useIsCategoryActive for a definition of what is an active category
 */
export const IsActive = ({ children, categoryId }: IsActiveProps) => {
  const isActive = useIsCategoryActive(categoryId);
  if (typeof children === 'function') return <>{children(isActive)}</>;
  if (!isActive) return null;
  return <>{children}</>;
};

type IsNotActiveProps = {
  categoryId?: string;
  children: ((isNotActive: boolean) => React.ReactNode) | React.ReactNode;
};
/**
 * Renders out the child content if the category either from the categoryId prop or if not provided the
 * current category is NOT active
 * If a render prop is passed as the child then a boolean will be provided with the is non-active state
 * NOTE: see useIsCategoryActive for a definition of what is an active category
 */
export const IsNotActive = ({ children, categoryId }: IsNotActiveProps) => {
  const isActive = useIsCategoryActive(categoryId);
  if (typeof children === 'function') {
    return <>{children(!isActive)}</>;
  }
  if (isActive) return null;
  return <>{children}</>;
};

export type CategoryLoopFunction = (
  category: ICategory,
  index: number
) => React.ReactNode;
export const isCategoryLoopFunction = (
  arg: any
): arg is CategoryLoopFunction => {
  return typeof arg === 'function';
};

/** Checks the type of the react child and returns in the correct way */
export function returnChild(
  children: CategoryLoopFunction | React.ReactNode,
  { category, index }: { category: ICategory; index: number }
) {
  return React.isValidElement(children)
    ? children
    : isCategoryLoopFunction(children)
    ? children(category, index)
    : null;
}

type LoopSubCategoriesProps = {
  categoryId?: string;
  children: CategoryLoopFunction | React.ReactNode;
};
/**
 * For each of the sub categories of the either the current category in context or the categoryId passed in
 * it repeats the children passed in.
 * So if you pass a render prop as a child it will be called with each sub category result.
 * Else if you pass in React Elements they will be repeated for each sub category result.
 */
export const LoopSubCategories: React.FC<LoopSubCategoriesProps> = ({
  children,
}) => {
  const subCategories = useSubCategories();
  return (
    <>
      {(subCategories || []).map((sub, index) => (
        <CategoryContext.Provider key={sub.id} value={sub}>
          {returnChild(children, {
            category: sub,
            index,
          })}
        </CategoryContext.Provider>
      ))}
    </>
  );
};
